import ApiService from './ApiService';
import { ENV } from '../environments/environment';

export interface ProtocolParameterField {
  id: number;
  element_type: string;
  type: string;
  label: string;
  values: string[] | null;
}

export interface ProtocolParameter {
  id: number;
  parameter_name: string;
  is_root: boolean;
  has_child: boolean;
  created_by: ProtocolAssignee | null;
  created_at: string;
  parameter_fields: ProtocolParameterField[];
  children: ProtocolParameter[];
}

export interface ProtocolAssignee {
  id: number;
  username: string;
}

export interface Protocol {
  id: number;
  requirement_doc_id: number;
  name: string;
  description: string;
  version: number;
  expected_time: number;
  /** QC: studies completed in fewer minutes than this are flagged as too fast */
  fast_threshold?: number;
  /** QC: studies completed in more minutes than this are flagged as too slow */
  slow_threshold?: number;
  effective_start_date?: string | null;
  status: 'Published' | 'Draft';
  is_active: boolean;
  ftp_location: string;
  created_by: ProtocolAssignee | null;
  created_at: string;
  updated_by: ProtocolAssignee | null;
  updated_at: string;
  parameters: ProtocolParameter[];
  assignees: ProtocolAssignee[];
}

export interface ProtocolListResponse {
  protocols: Protocol[];
  total: number;
  total_count?: number;
  total_pages?: number;
  page: number;
  size: number;
}

export interface GetProtocolsRequest {
  protocol_id?: string;
  requirement_doc_id?: string;
  status?: string;
  is_active?: string;
  client_id?: string;
  search?: string;
  page?: number;
  size?: number;
}

export interface CreateProtocolRequestField {
  element_type: string;
  type: string;
  label: string;
  values?: string[] | null;
}

export interface CreateProtocolRequestParam {
  parameter_name: string;
  is_root: boolean;
  parameter_fields?: CreateProtocolRequestField[];
  children?: CreateProtocolRequestParam[];
}

export interface CreateProtocolRequest {
  // protocol_id?: number; // optional for create
  requirement_doc_id: number;
  name: string;
  description: string;
  version: number;
  expected_time: string | number;
  fast_threshold?: number;
  slow_threshold?: number;
  status: 'Published' | 'Draft';
  is_active: boolean;
  assignee_ids?: number[];
  parameters: CreateProtocolRequestParam[];
}

export interface ApiResponse<T> {
  is_success: boolean;
  status: number;
  message: string;
  data: T;
}

export interface RequirementDocSummary {
  crd_id: string;
  work_order_id: number;
  title: string;
  is_active: boolean;
  id: number;
  status?: string; // Status of the CRD (APPROVED, PENDING, REJECTED, etc.)
}

export interface RequirementDocsListResponse {
  requirement_docs: RequirementDocSummary[];
  total: number;
  page: number;
  size: number;
}

class ProtocolService {
  private static instance: ProtocolService;
  private apiService: ApiService;

  public static getInstance(): ProtocolService {
    if (!ProtocolService.instance) {
      ProtocolService.instance = new ProtocolService();
    }
    return ProtocolService.instance;
  }

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getProtocols(params: GetProtocolsRequest = {}): Promise<ApiResponse<ProtocolListResponse[]>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    // Use mock data only if baseUrl is not configured
    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      // Dummy data for now since API is not deployed
      const allProtocols: Protocol[] = [
      {
        id: 1,
        requirement_doc_id: 1,
        name: "OCT-A Form",
        description: "Form for graders to record DR severity, CMT, fluid presence.",
        version: 1,
        expected_time: 30,
        effective_start_date: "2024-01-01",
        status: "Published",
        is_active: true,
        ftp_location: "FTPUploads/protocols/1/",
        created_by: { id: 1, username: "admin" },
        created_at: "2024-01-15T10:30:00Z",
        updated_by: { id: 1, username: "admin" },
        updated_at: "2024-01-20T14:22:00Z",
        assignees: [
          { id: 3, username: "retina_grader_1" },
          { id: 4, username: "james_sam" },
          { id: 5, username: "retina_grader_3" }
        ],
        parameters: [
          {
            id: 11,
            parameter_name: "CHOROIDAL THICKNESS",
            is_root: true,
            has_child: true,
            created_by: { id: 1, username: "admin" },
            created_at: "2024-01-15T10:30:00Z",
            parameter_fields: [
              {
                id: 21,
                element_type: "TextBox",
                type: "Numeric",
                label: "Numeric",
                values: null
              }
            ],
            children: [
              {
                id: 12,
                parameter_name: "S/F CT",
                is_root: false,
                has_child: false,
                created_by: { id: 1, username: "admin" },
                created_at: "2024-01-15T10:30:00Z",
                parameter_fields: [
                  {
                    id: 23,
                    element_type: "TextBox",
                    type: "Numeric",
                    label: "Numeric",
                    values: null
                  }
                ],
                children: []
              }
            ]
          }
        ]
      },
      {
        id: 2,
        requirement_doc_id: 2,
        name: "Retinal Imaging Protocol",
        description: "Standard protocol for retinal imaging analysis and grading.",
        version: 2,
        expected_time: 25,
        effective_start_date: "2024-02-01",
        status: "Draft",
        is_active: true,
        ftp_location: "FTPUploads/protocols/2/",
        created_by: { id: 1, username: "admin" },
        created_at: "2024-01-20T09:15:00Z",
        updated_by: { id: 1, username: "admin" },
        updated_at: "2024-01-25T11:45:00Z",
        assignees: [
          { id: 3, username: "retina_grader_1" },
          { id: 4, username: "james_sam" }
        ],
        parameters: [
          {
            id: 21,
            parameter_name: "MACULAR THICKNESS",
            is_root: true,
            has_child: false,
            created_by: { id: 1, username: "admin" },
            created_at: "2024-01-20T09:15:00Z",
            parameter_fields: [
              {
                id: 31,
                element_type: "TextBox",
                type: "Numeric",
                label: "Thickness (μm)",
                values: null
              }
            ],
            children: []
          }
        ]
      },
      {
        id: 3,
        requirement_doc_id: 3,
        name: "Diabetic Retinopathy Assessment",
        description: "Comprehensive assessment protocol for diabetic retinopathy grading.",
        version: 1,
        expected_time: 35,
        effective_start_date: "2024-01-10",
        status: "Published",
        is_active: false,
        ftp_location: "FTPUploads/protocols/3/",
        created_by: { id: 2, username: "grader2" },
        created_at: "2024-01-10T14:20:00Z",
        updated_by: { id: 2, username: "grader2" },
        updated_at: "2024-01-18T16:30:00Z",
        assignees: [
          { id: 3, username: "retina_grader_1" }
        ],
        parameters: [
          {
            id: 31,
            parameter_name: "RETINAL VESSELS",
            is_root: true,
            has_child: true,
            created_by: { id: 2, username: "grader2" },
            created_at: "2024-01-10T14:20:00Z",
            parameter_fields: [
              {
                id: 41,
                element_type: "DropDown",
                type: "Text",
                label: "Vessel Status",
                values: ["Normal", "Abnormal", "Severe"]
              }
            ],
            children: [
              {
                id: 32,
                parameter_name: "MICROANEURYSMS",
                is_root: false,
                has_child: false,
                created_by: { id: 2, username: "grader2" },
                created_at: "2024-01-10T14:20:00Z",
                parameter_fields: [
                  {
                    id: 42,
                    element_type: "CheckBox",
                    type: "Text",
                    label: "Present",
                    values: ["Yes", "No"]
                  }
                ],
                children: []
              }
            ]
          }
        ]
      },
      {
        id: 4,
        requirement_doc_id: 4,
        name: "Glaucoma Screening Form",
        description: "Standardized form for glaucoma screening and assessment.",
        version: 1,
        expected_time: 40,
        effective_start_date: "2024-03-01",
        status: "Draft",
        is_active: true,
        ftp_location: "FTPUploads/protocols/4/",
        created_by: { id: 3, username: "grader3" },
        created_at: "2024-02-15T11:00:00Z",
        updated_by: { id: 3, username: "grader3" },
        updated_at: "2024-02-20T13:15:00Z",
        assignees: [
          { id: 4, username: "james_sam" },
          { id: 5, username: "retina_grader_3" }
        ],
        parameters: [
          {
            id: 41,
            parameter_name: "INTRAOCULAR PRESSURE",
            is_root: true,
            has_child: false,
            created_by: { id: 3, username: "grader3" },
            created_at: "2024-02-15T11:00:00Z",
            parameter_fields: [
              {
                id: 51,
                element_type: "TextBox",
                type: "Numeric",
                label: "IOP (mmHg)",
                values: null
              }
            ],
            children: []
          }
        ]
      },
      {
        id: 5,
        requirement_doc_id: 5,
        name: "Macular Degeneration Protocol",
        description: "Assessment protocol for age-related macular degeneration.",
        version: 3,
        expected_time: 28,
        effective_start_date: "2024-01-05",
        status: "Published",
        is_active: true,
        ftp_location: "FTPUploads/protocols/5/",
        created_by: { id: 1, username: "admin" },
        created_at: "2024-01-05T08:45:00Z",
        updated_by: { id: 1, username: "admin" },
        updated_at: "2024-01-22T10:30:00Z",
        assignees: [
          { id: 3, username: "retina_grader_1" },
          { id: 4, username: "james_sam" },
          { id: 6, username: "retina_grader_4" }
        ],
        parameters: [
          {
            id: 51,
            parameter_name: "DRUSEN ASSESSMENT",
            is_root: true,
            has_child: true,
            created_by: { id: 1, username: "admin" },
            created_at: "2024-01-05T08:45:00Z",
            parameter_fields: [
              {
                id: 61,
                element_type: "DropDown",
                type: "Text",
                label: "Drusen Type",
                values: ["Hard", "Soft", "Mixed"]
              }
            ],
            children: [
              {
                id: 52,
                parameter_name: "DRUSEN SIZE",
                is_root: false,
                has_child: false,
                created_by: { id: 1, username: "admin" },
                created_at: "2024-01-05T08:45:00Z",
                parameter_fields: [
                  {
                    id: 62,
                    element_type: "TextBox",
                    type: "Text",
                    label: "Size Category",
                    values: null
                  }
                ],
                children: []
              }
            ]
          }
        ]
      }
    ];

    // Apply filters
    let filteredProtocols = allProtocols;

    // Filter by status
    if (params.status && params.status !== 'all') {
      filteredProtocols = filteredProtocols.filter(p => p.status === params.status);
    }

    // Filter by is_active
    if (params.is_active && params.is_active !== 'all') {
      const isActiveValue = params.is_active === 'true';
      filteredProtocols = filteredProtocols.filter(p => p.is_active === isActiveValue);
    }

    // (Dev/mock only) Filter by client_id if provided.
    // Note: Protocol mock data above does not currently include client_id,
    // so this will be a no-op unless mock data is extended.
    if (params.client_id && params.client_id !== 'all') {
      const clientIdNum = Number(params.client_id);
      if (!Number.isNaN(clientIdNum)) {
        // @ts-ignore - protocols may carry client_id in real data
        filteredProtocols = filteredProtocols.filter(p => Number((p as any).client_id) === clientIdNum);
      }
    }

    // Filter by search
    if (params.search && params.search !== 'all') {
      const searchLower = params.search.toLowerCase();
      filteredProtocols = filteredProtocols.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
      );
    }

    // Apply pagination
    const page = params.page || 1;
    const size = params.size || 10;
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const paginatedProtocols = filteredProtocols.slice(startIndex, endIndex);

    const response: ProtocolListResponse = {
      protocols: paginatedProtocols,
      total: filteredProtocols.length,
      page: page,
      size: size
    };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        is_success: true,
        status: 200,
        message: "Data fetched successfully",
        data: [response]
      };
    }

    // Production: use real API
    const searchParams = new URLSearchParams();
    searchParams.append('protocol_id', params.protocol_id || 'all');
    searchParams.append('requirement_doc_id', params.requirement_doc_id || 'all');
    searchParams.append('status', params.status || 'all');
    searchParams.append('is_active', params.is_active || 'all');
    // Only include client_id when a specific client is selected
    if (params.client_id && params.client_id !== 'all') {
      searchParams.append('client_id', String(params.client_id));
    }
    searchParams.append('page', String(params.page || 1));
    searchParams.append('size', String(params.size || 10));
    // Only include search when user provided a value; otherwise omit
    if (params.search !== undefined && params.search !== null && String(params.search).trim() !== '') {
      searchParams.append('search', String(params.search));
    }

    const endpoint = `/protocols?${searchParams.toString()}`;

    const response = await this.apiService.get<ApiResponse<ProtocolListResponse[]>>(
      endpoint,
      undefined,
      baseUrl
    );

    if (response.success && response.data) {
      // Keep the original external contract: return backend ApiResponse<T>
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to fetch protocols',
      // @ts-ignore - on failure we return an empty array for data
      data: [],
    };
  }

  async manageProtocolUsers(payload: { protocol_id: number; user_ids: number[]; is_deleted?: boolean }): Promise<ApiResponse<null>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();
    const response = await this.apiService.post<ApiResponse<null>>(
      `/protocols/manage-users`,
      payload,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to update protocol users',
      // @ts-ignore
      data: null,
    };
  }

  async getRequirementDocs(): Promise<ApiResponse<RequirementDocsListResponse[]>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      // Mock minimal structure to avoid runtime errors in dev without API
      const mock: RequirementDocsListResponse = {
        requirement_docs: [
          { crd_id: 'CRD-RETINA-001', work_order_id: 1, title: 'Sample', is_active: true, id: 1 },
          { crd_id: 'CRD-RETINA-002', work_order_id: 2, title: 'Sample 2', is_active: true, id: 2 },
        ],
        total: 2,
        page: 1,
        size: 100,
      };
      return {
        is_success: true,
        status: 200,
        message: 'Mock CRD list',
        data: [mock],
      };
    }

    const endpoint = `/crd?requirement_doc_id=all&crd_id=all`;
    const response = await this.apiService.get<ApiResponse<RequirementDocsListResponse[]>>(
      endpoint,
      undefined,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to fetch requirement documents',
      // @ts-ignore
      data: [],
    };
  }

  async deleteProtocol(protocolId: number): Promise<ApiResponse<any>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    // Use mock data only if baseUrl is not configured
    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      return {
        is_success: true,
        status: 200,
        message: "Protocol deleted successfully",
        data: { id: protocolId }
      };
    }

    // Production: use real API with query parameter
    const response = await this.apiService.delete<ApiResponse<any>>(
      `/protocols/protocol_id=${protocolId}`,
      undefined,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to delete protocol',
      data: null,
    };
  }

  async createProtocol(payload: CreateProtocolRequest): Promise<ApiResponse<any>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      // Simulate create in dev when no API configured
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        is_success: true,
        status: 201,
        message: 'Protocol created successfully (mock)',
        // @ts-ignore
        data: [{ id: Math.floor(Math.random() * 10000), ...payload }],
      } as ApiResponse<any>;
    }

    const response = await this.apiService.post<ApiResponse<any>>(
      `/protocols`,
      payload,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to create protocol',
      data: null,
    };
  }

  async updateProtocol(protocolId: number, payload: Partial<CreateProtocolRequest>): Promise<ApiResponse<any>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        is_success: true,
        status: 200,
        message: 'Protocol updated successfully (mock)',
        // @ts-ignore
        data: [{ id: protocolId, ...payload }],
      } as ApiResponse<any>;
    }

    const response = await this.apiService.put<ApiResponse<any>>(
      `/protocols/${protocolId}`,
      payload,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to update protocol',
      data: null,
    };
  }

  async upgradeProtocol(payload: any): Promise<ApiResponse<any>> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      // Simulate upgrade in dev when no API configured
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        is_success: true,
        status: 200,
        message: 'Protocol upgraded successfully (mock)',
        // @ts-ignore
        data: payload.protocol_id || 'mock-upgrade-id',
      } as ApiResponse<any>;
    }

    const response = await this.apiService.post<ApiResponse<any>>(
      `/protocols/upgrade`,
      payload,
      baseUrl
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to upgrade protocol',
      data: null,
    };
  }

  private async getPdfRequestHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: 'application/pdf, application/octet-stream, */*',
    };

    let token: string | null = null;
    try {
      const SecureStorageService = (await import('./SecureStorageService')).default;
      const secureStorage = SecureStorageService.getInstance();
      token =
        (await secureStorage.getLocalItem('token')) ||
        (await secureStorage.getSessionItem('auth_token'));
    } catch {
      token = localStorage.getItem('token') || sessionStorage.getItem('auth_token');
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async generateProtocolReport(
    protocolId: number | string
  ): Promise<{ success: boolean; message: string }> {
    const baseUrl = this.apiService.getProtocolServiceUrl();

    if (!baseUrl || baseUrl === '' || baseUrl === 'undefined') {
      return {
        success: false,
        message: 'Protocol service URL is not configured.',
      };
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const queryParams = new URLSearchParams({
      protocol_id: String(protocolId),
    });
    const url = `${normalizedBase}protocols/generate-report?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getPdfRequestHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to generate protocol report. Please try again.';

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        return { success: false, message: errorMessage };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let fileName: string | null = null;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch?.[1]) {
          fileName = filenameMatch[1].replace(/['"]/g, '');
          if (fileName.startsWith("UTF-8''")) {
            fileName = decodeURIComponent(fileName.substring(7));
          } else if (fileName.includes("''")) {
            const parts = fileName.split("''");
            if (parts.length > 1) {
              fileName = decodeURIComponent(parts[parts.length - 1]);
            }
          }
        }
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || `protocol-${protocolId}-report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return {
        success: true,
        message: 'Protocol report downloaded successfully.',
      };
    } catch (error) {
      console.error('Error generating protocol report:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred while generating the protocol report.',
      };
    }
  }
}

export default ProtocolService;
